<template>
	<VueShowdown
		:markdown="content"
		:flavor="flavor"
		:options="options"
		:extensions="extensions"
		:vue-template="vueTemplate"
		class="flow-markdown"
	/>
</template>
<script>
import { defineComponent } from "vue";
import { VueShowdown } from "vue-showdown";

export default defineComponent({
	name: "Markdown",
	components: {
		VueShowdown
	},
	props: {
		/**
		 * Raw markdown content
		 */
		content: {
			type: String,
			required: false,
			default: null
		},

		/**
		 * Showdown flavor
		 *
		 * 'github' | 'original' | 'ghost' | 'vanilla' | 'allOn'
		 */
		flavor: {
			type: String,
			required: false,
			default: null
		},

		/**
		 * Showdown options
		 *
		 */
		options: {
			type: Object,
			required: false,
			default: () => ({})
		},

		/**
		 * Showdown extensions
		 *
		 */
		extensions: {
			type: Array,
			required: false,
			default: null
		},

		/**
		 * Treat the HTML string as Vue template. Require full build of Vue (runtime + complier)
		 */
		vueTemplate: {
			type: Boolean,
			required: false,
			default: false
		}
	}
});
</script>
<style lang="scss">
div.flow-markdown {
	color: var(--dashboard-font-dark);
	p {
		color: var(--dashboard-font-light);
	}
	a {
		color: var(--dashboard-primary);
	}

	hr {
		border-color: var(--dashboard-border);
		border-style: solid;
		border-width: 1px;
	}
}

.sideout-container {
	div.flow-markdown {
		color: var(--slideout-font-dark);

		p {
			color: var(--slideout-font-light);
		}
		a {
			color: var(--slideout-primary);
		}
		hr {
			border-color: var(--slideout-border);
		}
	}
}

$flow-md-body-font-size: 16px !default;
$flow-md-code-font-size: 12px !default;
$flow-md-body-font-family: var(--body-font) !default;
$flow-md-code-font-family: var(--body-font) !default;
$flow-md-input-font-family: var(--body-font);
$flow-md-header-font-family: var(--body-font) !default;
$flow-md-header-border: "false" !default;

@mixin markdown-mixin(
	$flow-md-body-color,
	$flow-md-link-color,
	$flow-md-border,
	$flow-md-element-light,
	$flow-md-font-light,
	$flow-md-element
) {
	div.flow-markdown {
		/* anchor tag */

		a {
			color: $flow-md-link-color;
		}

		h1,
		h2 {
			@if $flow-md-header-border == "true" {
				border-bottom: 1px solid $flow-md-border;
			}
		}

		h3,
		h4 {
			color: $flow-md-link-color;
		}

		code {
			background-color: $flow-md-element-light;
		}

		pre {
			background-color: $flow-md-element-light;
		}

		kbd {
			background-color: $flow-md-element-light;
			background-image: -webkit-linear-gradient($flow-md-element-light, $flow-md-element-light);
			background-image: linear-gradient($flow-md-element-light, $flow-md-element-light);

			color: $flow-md-body-color;
			border: 1px solid $flow-md-border;

			box-shadow: 0 1px 0 $flow-md-element;
		}

		blockquote {
			color: $flow-md-font-light;
			border-left: 4px solid $flow-md-border;
			p {
				color: $flow-md-font-light;
			}
		}

		hr {
			background-color: $flow-md-border;
		}

		table {
			th,
			td {
				border: 1px solid $flow-md-border;
			}

			tr {
				background-color: $flow-md-element-light;
				border-top: 1px solid $flow-md-border;

				&:nth-child(2n) {
					background-color: $flow-md-element;
				}
			}
		}
		ul {
			li {
				&:before {
					color: $flow-md-link-color;
				}
			}
		}

		ul,
		ol {
			li {
				color: $flow-md-body-color;
			}
		}
	}
}

div.flow-markdown {
	-ms-text-size-adjust: 100%;
	-webkit-text-size-adjust: 100%;
	overflow: hidden;
	font-family: $flow-md-body-font-family;
	font-size: $flow-md-body-font-size;
	// line-height: 1.6;
	word-wrap: break-word;

	* {
		-moz-box-sizing: border-box;
		box-sizing: border-box;
	}

	> *:first-child {
		margin-top: 0 !important;
	}
	> *:last-child {
		margin-bottom: 0 !important;
	}

	/* anchor tag */

	a {
		background: transparent;
		text-decoration: none;
		word-break: break-all;

		&:active,
		&:hover {
			outline: 0;
		}

		&:hover,
		&:focus,
		&:active {
			text-decoration: underline;
		}
	}

	h1,
	h2,
	h3,
	h4,
	h6 {
		line-height: 1.7;
		margin-top: 24px;
		margin-bottom: 16px;
	}
	h5 {
		line-height: 1.7;
		margin-top: 24px;
		margin-bottom: 4px;
		font-weight: normal;
	}

	h1 {
		// font-size: 24px;
		font-weight: 400;
	}
	h2 {
		// font-size: 22px;
		font-weight: 500;
	}
	h3 {
		// font-size: 20px;
		font-weight: 400;
	}

	h4 {
		font-size: 12px;
		margin-bottom: 8px !important;
	}

	// h5 {
	// 	font-size: 16px;
	// }

	// h6 {
	// 	font-size: 14px;
	// }

	/* defaults for strong/em, just in case */

	strong {
		font-weight: bold;
	}
	em {
		font-style: italic;
	}

	/* same margins for all typographic block elements */
	p {
		line-height: 1.5;
		font-size: 14px;
	}
	p,
	blockquote,
	ul,
	ol,
	dl,
	table,
	pre {
		margin-top: 0;
		margin-bottom: $flow-md-body-font-size;
	}

	h1,
	h2 {
		padding-bottom: 4px;
	}

	/* pre/code */

	code,
	pre,
	kbd {
		font-family: $flow-md-code-font-family;
		font-size: $flow-md-code-font-size;
	}

	code {
		padding: 4px 0;
		margin: 0;
		font-size: 85%;
		border-radius: 4px;

		&:before,
		&:after {
			content: "\00a0";
		}
	}

	pre {
		overflow: auto;
		word-wrap: normal;
		padding: $flow-md-body-font-size;
		overflow: auto;
		font-size: 85%;
		line-height: 1.45;
		border-radius: 3px;

		code {
			display: inline;
			max-width: initial;
			padding: 0;
			margin: 0;
			overflow: initial;
			line-height: inherit;
			word-wrap: normal;
			background: transparent;

			&:before,
			&:after {
				content: normal;
			}
		}

		> code {
			font-size: 1em;
			word-break: normal;
			white-space: pre;
			border: 0;
		}
	}

	kbd {
		background-repeat: repeat-x;
		display: inline-block;
		padding: 4px;
		margin: 0 1px;
		font-size: 10px;
		line-height: 10px;
		border-radius: 2px;
	}

	/* forms */

	input {
		color: inherit;
		font: inherit;
		margin: 0;
		line-height: normal;
		font-size: 13px;
		line-height: 1.4;
		font-family: $flow-md-input-font-family;

		&[disabled] {
			cursor: default;
		}

		&[type="checkbox"] {
			-moz-box-sizing: border-box;
			box-sizing: border-box;
			padding: 0;
		}
	}

	/* blockquotes */

	blockquote {
		margin: 0 0 $flow-md-body-font-size;
		padding: 0 15px;

		& > :first-child {
			margin-top: 0;
		}
		& > :last-child {
			margin-bottom: 0;
		}
	}

	/* images */

	img {
		border: 0;
		max-width: 100%;
		-moz-box-sizing: border-box;
		box-sizing: border-box;
	}

	s,
	del {
		text-decoration: line-through;
	}

	/* horizontal rules */

	hr {
		-moz-box-sizing: content-box;
		box-sizing: content-box;
		height: 0;
		overflow: hidden;
		background: transparent;
		height: 1px;
		padding: 0;
		margin: $flow-md-body-font-size 0;
		border: 0 none;

		&:before {
			display: table;
			content: "";
		}

		&:after {
			display: table;
			clear: both;
			content: "";
		}
	}

	/* tables */

	td,
	th {
		padding: 0;
	}

	table {
		border-collapse: collapse;
		border-spacing: 0;
		display: block;
		width: 100%;
		overflow: auto;
		word-break: normal;
		word-break: keep-all;

		th,
		td {
			padding: 6px 13px;
		}

		th {
			font-weight: bold;
		}
	}

	/* lists */

	ul,
	ol {
		padding: 0;
		padding-left: 2em;
		font-size: 14px;

		ol {
			list-style-type: lower-roman;
		}

		ul,
		ol {
			margin-top: 0;
			margin-bottom: 0;

			ol {
				list-style-type: lower-alpha;
			}
		}
	}

	ul {
		li {
			position: relative;
			&:before {
				content: "\2022";
				font-weight: bold;
				display: inline-flex;
				width: 1em;
				margin-left: -1em;
			}
		}
		// > li {
		// 	list-style-type: disc;
		// 	> ul > li {
		// 		list-style-type: circle;
		// 	}
		// }
	}
	ol {
		> li {
			list-style-type: decimal;
		}
	}

	li > p {
		margin-top: $flow-md-body-font-size;
	}

	/* definitions */

	dd {
		margin-left: 0;
	}

	dl {
		padding: 0;

		dt {
			padding: 0;
			margin-top: $flow-md-body-font-size;
			font-size: 1em;
			font-style: italic;
			font-weight: bold;
		}

		dd {
			padding: 0 $flow-md-body-font-size;
			margin-bottom: $flow-md-body-font-size;
		}
	}
}

@include markdown-mixin(
	var(--dashboard-font),
	var(--dashboard-primary),
	var(--dashboard-border),
	var(--dashboard-element-light),
	var(--dashboard-font-light),
	var(--dashboard-element)
);
.sideout-container {
	@include markdown-mixin(
		var(--slideout-font),
		var(--slideout-primary),
		var(--slideout-border),
		var(--slideout-element-light),
		var(--slideout-font-light),
		var(--slideout-element)
	);
}
</style>
